import React, { useState, useEffect } from 'react';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { C1SignupData } from '../C1SignupFlow';
import {
  useStripePayment,
  storePaymentState,
  clearPaymentState,
} from '../../../hooks/useStripePayment';
import { mapPlanToPackageId, CreditPackageId } from '../../../services/stripePaymentService';
import './CardDetails.css';

/** Parse Australian address string into components for Stripe billing_details */
function parseAustralianAddress(addressStr: string): {
  line1: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
} {
  // Default values
  const result = {
    line1: addressStr || '',
    city: '',
    state: '',
    postal_code: '',
    country: 'AU',
  };

  if (!addressStr) return result;

  // Try to extract state (NSW, VIC, QLD, SA, WA, TAS, NT, ACT)
  const stateMatch = addressStr.match(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/i);
  if (stateMatch) {
    result.state = stateMatch[1].toUpperCase();
  }

  // Try to extract postcode (4 digits)
  const postcodeMatch = addressStr.match(/\b(\d{4})\b/);
  if (postcodeMatch) {
    result.postal_code = postcodeMatch[1];
  }

  // Extract street address (everything before state/postcode)
  let street = addressStr
    .replace(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/gi, '')
    .replace(/\b\d{4}\b/g, '')
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Try to extract city (common Australian cities or last word before state)
  const cityPatterns = /\b(Sydney|Melbourne|Brisbane|Perth|Adelaide|Hobart|Darwin|Canberra|Gold Coast|Newcastle|Wollongong|Geelong|Townsville|Cairns|Toowoomba|Ballarat|Bendigo|Albury|Launceston|Mackay|Rockhampton|Bunbury|Bundaberg|Hervey Bay|Wagga Wagga|Mildura|Shepparton|Gladstone|Port Macquarie|Tamworth|Orange|Dubbo|Geraldton|Nowra|Bathurst|Warrnambool|Albany|Kalgoorlie|Devonport|Mount Gambier|Lismore|Nelson Bay)\b/i;
  const cityMatch = street.match(cityPatterns);
  if (cityMatch) {
    result.city = cityMatch[1];
    street = street.replace(cityPatterns, '').replace(/\s+/g, ' ').trim();
  }

  result.line1 = street || addressStr;

  return result;
}

interface StepProps {
  data: C1SignupData;
  onNext: (stepData: Partial<C1SignupData>) => void;
  onCancel: () => void;
}

interface FormProps extends StepProps {
  paymentIntentId: string;
  packageId: CreditPackageId;
  confirmWithBackend: (paymentIntentId: string) => Promise<{ success: boolean; credits: number }>;
}

// ── Inner form — rendered inside <Elements> with a valid clientSecret ─────────

const CardDetailsForm: React.FC<FormProps> = ({ data, onNext, onCancel, paymentIntentId, packageId, confirmWithBackend }) => {
  const stripe = useStripe();
  const elements = useElements();

  const email = data.partyEmail || '';

  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  // Handle redirect return from payment methods like Klarna
  useEffect(() => {
    if (!stripe) return;

    const urlParams = new URLSearchParams(window.location.search);
    const paymentRedirect = urlParams.get('payment_redirect');
    const redirectStatus = urlParams.get('redirect_status');
    const paymentIntentClientSecret = urlParams.get('payment_intent_client_secret');

    if ((paymentRedirect === 'true' || redirectStatus) && paymentIntentClientSecret) {
      // Clean up URL
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);

      // Retrieve the payment intent to check its status
      setIsProcessing(true);
      stripe.retrievePaymentIntent(paymentIntentClientSecret).then(async ({ paymentIntent, error }) => {
        if (error) {
          setErrors({ card: error.message || 'Failed to verify payment status.' });
          setIsProcessing(false);
          return;
        }

        if (paymentIntent?.status === 'succeeded') {
          clearPaymentState();
          try {
            const confirmation = await confirmWithBackend(paymentIntent.id);
            onNext({
              stripeBillingEmail: email,
              stripePaymentMethodId: paymentIntent.payment_method as string,
              creditsPurchased: confirmation.credits,
            });
          } catch (err: any) {
            setErrors({ submit: err.message || 'Payment succeeded but confirmation failed.' });
            setIsProcessing(false);
          }
        } else if (paymentIntent?.status === 'processing') {
          clearPaymentState();
          onNext({
            stripeBillingEmail: email,
            stripePaymentMethodId: paymentIntent.payment_method as string,
          });
        } else if (paymentIntent?.status === 'requires_payment_method') {
          setErrors({ card: 'Payment was not completed. Please try again.' });
          setIsProcessing(false);
        } else {
          setErrors({ card: `Payment status: ${paymentIntent?.status}. Please try again.` });
          setIsProcessing(false);
        }
      });
    }
  }, [stripe, email, confirmWithBackend, onNext]);

  // Warn user before leaving during payment
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isProcessing) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isProcessing]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isProcessing || !stripe || !elements) return;

    setIsProcessing(true);
    storePaymentState({ email, packageId, paymentIntentId, status: 'processing', timestamp: Date.now() });

    try {
      // Parse address for billing details (required for Klarna and other BNPL methods)
      const parsedAddress = parseAustralianAddress(data.partyAddress || '');
      
      // Build full name from first/last name or fall back to party name
      const fullName = data.adminFirstName && data.adminLastName
        ? `${data.adminFirstName} ${data.adminLastName}`
        : data.partyName || 'Customer';

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/signup?payment_redirect=true`,
          payment_method_data: {
            billing_details: {
              email,
              name: fullName,
              phone: data.adminPhone || '',
              address: {
                line1: parsedAddress.line1,
                city: parsedAddress.city || 'Sydney', // Default city if not parsed
                state: parsedAddress.state || 'NSW', // Default state if not parsed
                postal_code: parsedAddress.postal_code || '2000', // Default postcode if not parsed
                country: 'AU',
              },
            },
          },
        },
      });

      if (error) {
        storePaymentState({ email, packageId, paymentIntentId, status: 'failed', timestamp: Date.now() });
        setErrors({ card: error.message || 'Payment failed. Please try again.' });
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        clearPaymentState();
        const confirmation = await confirmWithBackend(paymentIntent.id);
        onNext({
          stripeBillingEmail: email,
          stripePaymentMethodId: paymentIntent.payment_method as string,
          creditsPurchased: confirmation.credits,
        });
      } else if (paymentIntent?.status === 'processing') {
        // BECS bank transfers are async — payment settles in 2-3 business days.
        // The backend webhook (payment_intent.succeeded) handles account activation.
        clearPaymentState();
        onNext({
          stripeBillingEmail: email,
          stripePaymentMethodId: paymentIntent.payment_method as string,
        });
      } else {
        setErrors({ card: `Payment status: ${paymentIntent?.status}. Please try again.` });
        setIsProcessing(false);
      }
    } catch (err: any) {
      setErrors({ submit: err.message || 'An error occurred. Please try again.' });
      setIsProcessing(false);
    }
  };

  return (
    <div className="card-details-wrapper">
      {showDuplicateWarning && (
        <div
          className="duplicate-payment-warning"
          style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 8px', color: '#856404' }}>Payment May Be In Progress</h4>
              <p style={{ margin: '0 0 12px', color: '#856404', fontSize: '14px' }}>
                We detected that a payment may have been started but not completed.
                If you recently submitted a payment, please check your email for a confirmation
                before submitting again to avoid duplicate charges.
              </p>
              <button
                type="button"
                onClick={() => { setShowDuplicateWarning(false); clearPaymentState(); }}
                className="btn-secondary"
                style={{ padding: '8px 16px', fontSize: '14px' }}
              >
                I understand, proceed anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="card-details-form">
        <div className="form-section">
          <div className="form-section-title">📧 Billing Details</div>
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email Address</label>
            <input
              type="email"
              id="email"
              className="form-input"
              data-testid="card-details-email-input"
              value={email}
              readOnly
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', color: '#555' }}
            />
            <div className="form-hint" style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              This is the email from your Party Details
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-section-title">💳 Payment Details</div>
          <PaymentElement
            onReady={() => setPaymentElementReady(true)}
            options={{
              layout: 'tabs',
              fields: { billingDetails: { email: 'never' } },
              wallets: { link: 'never' },
              defaultValues: {
                billingDetails: {
                  address: { country: 'AU' },
                },
              },
            }}
          />
        </div>

        <div className="pci-compliance-disclaimer">
          <div className="pci-header">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span className="pci-title">PCI Compliance & Security</span>
          </div>
          <div className="pci-content">
            <p className="pci-statement">
              This payment form is PCI DSS Level 1 compliant. Your card information is processed securely through Stripe.
            </p>
            <ul className="pci-guarantees">
              <li>Your card details are encrypted and never stored on our servers</li>
              <li>All transactions are processed over secure HTTPS connections</li>
              <li>Sensitive data is tokenized and handled by PCI-compliant payment processors</li>
              <li>Your payment information is protected with industry-standard security measures</li>
            </ul>
            <p className="pci-footer">
              By providing your card details, you acknowledge that you have read and agree to our payment processing terms.
            </p>
          </div>
        </div>

        {errors.card && <div className="error-alert">{errors.card}</div>}
        {errors.submit && <div className="error-alert">{errors.submit}</div>}

        {data.stripePlanType && (
          <div className="billing-summary">
            <div className="summary-item">
              <span>Plan:</span>
              <span className="summary-value">{data.stripePlanType}</span>
            </div>
            <div className="summary-item">
              <span>Email:</span>
              <span className="summary-value">{email || 'Not provided'}</span>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn-secondary" data-testid="card-details-cancel-btn" onClick={onCancel} disabled={isProcessing}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" data-testid="card-details-submit-btn" disabled={isProcessing || !stripe || !elements || !paymentElementReady}>
            {isProcessing ? 'Processing...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Wrapper — uses useStripePayment hook ──────────────────────────────────────

const CardDetails: React.FC<StepProps> = (props) => {
  const email = props.data.partyEmail || '';
  const planType = props.data.stripePlanType || 'STARTER';

  const {
    status,
    error,
    stripePromise,
    clientSecret,
    paymentIntentId,
    packageId,
    confirmWithBackend,
  } = useStripePayment({
    email,
    customerName: props.data.partyName || 'Customer',
    planType,
    partyAbn: props.data.partyAbn,
    partyType: props.data.partyType,
    onAlreadyPaid: (data) => {
      props.onNext({
        stripeBillingEmail: data.email,
        stripePaymentMethodId: data.paymentMethodId,
        creditsPurchased: data.credits,
      });
    },
  });

  if (status === 'loading') {
    return (
      <div className="card-details-wrapper" style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '24px', marginBottom: '12px' }}>⏳</div>
        <p>Loading payment form...</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="card-details-wrapper" style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '24px', marginBottom: '12px' }}>⚠️</div>
        <p style={{ color: '#dc2626' }}>{error}</p>
        <button className="btn-primary" style={{ marginTop: '16px' }} onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  if (status === 'already-paid' || !clientSecret || !stripePromise) return null;

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret, appearance: { theme: 'stripe' } }}
    >
      <CardDetailsForm
        {...props}
        paymentIntentId={paymentIntentId!}
        packageId={packageId}
        confirmWithBackend={confirmWithBackend}
      />
    </Elements>
  );
};

export default CardDetails;
